/**
 * src/engine/runJob.js
 *
 * Core job executor. Called by useEngine for each job.
 *
 * @param {object} job          - job config object
 * @param {object} opts
 *   @param {object}   opts.state
 *   @param {function} opts.updateState
 *   @param {string}   opts.tenantId
 *   @param {object}   opts.session
 *   @param {string}   opts.triggeredBy  - 'schedule' | 'condition' | 'event' | 'manual'
 *   @param {object}   [opts.eventPayload]
 *   @param {function} [opts.onComplete] - called with run log entry
 */

import { now, generateId } from '../utils/formatting.js';
import { getMatchingEntities, executeStateLogic, executeVendorSync } from './jobRegistry.js';
import { writeRunLog } from '../api/engineApi.js';

// last_run_at / last_run_result on engine_jobs are stamped by
// engine-run-log-write (recordCompletedRun) — runJob no longer
// re-persists the catalogue row so we avoid a write race against
// concurrent edits in JobFormModal.

export async function runJob(
  job,
  { state, updateState, tenantId, session, triggeredBy, eventPayload, onComplete }
) {
  if (!job.enabled) return null;

  const startedAt = now();
  const runId = 'run_' + generateId();

  try {
    let matchedEntities = [];

    // ── Resolve entities ──────────────────────────────────────────────────────
    if (job.trigger.type === 'condition' || job.trigger.type === 'schedule') {
      if (job.trigger.condition) {
        matchedEntities = getMatchingEntities(job.trigger, state);
      }
    } else if (job.trigger.type === 'event') {
      // For event jobs, the payload carries the entity
      if (eventPayload?.doc) matchedEntities = [{ ...eventPayload.doc, _entityType: 'document' }];
      if (eventPayload?.task) matchedEntities = [{ ...eventPayload.task, _entityType: 'task' }];
      if (eventPayload?.risk) matchedEntities = [{ ...eventPayload.risk, _entityType: 'risk' }];
      if (eventPayload?.vendor)
        matchedEntities = [{ ...eventPayload.vendor, _entityType: 'vendor' }];
    }
    // manual: matchedEntities stays [] — job runs with no entity context

    // ── Execute action ────────────────────────────────────────────────────────
    let result = { changed: false, summary: 'No action executed', mutations: [] };

    if (job.action.type === 'state_logic') {
      result = executeStateLogic(job, matchedEntities, state);
    } else if (job.action.type === 'vendor_sync') {
      result = await executeVendorSync(job, state, { tenantId });
    }

    // Apply state mutations
    if (result.mutations.length > 0) {
      result.mutations.forEach((mutate) => updateState(mutate));
    }

    // ── Write run log ─────────────────────────────────────────────────────────
    const logEntry = {
      id: runId,
      jobId: job.id,
      tenantId: tenantId,
      triggeredBy,
      triggeredByUserId: session?.userId ?? null,
      startedAt,
      finishedAt: now(),
      result: result.changed ? 'success' : 'no_change',
      summary: result.summary,
      errorMessage: null,
    };

    if (job.logRuns && (!job.logOnlyIfChanged || result.changed)) {
      await writeRunLog(logEntry).catch(() => {}); // non-blocking
    }

    if (onComplete) onComplete(logEntry);
    return logEntry;
  } catch (err) {
    console.error('[runJob] job failed:', job.id, err);

    const logEntry = {
      id: runId,
      jobId: job.id,
      tenantId,
      triggeredBy,
      triggeredByUserId: session?.userId ?? null,
      startedAt,
      finishedAt: now(),
      result: 'error',
      summary: '',
      errorMessage: err.message,
    };

    if (job.logRuns) await writeRunLog(logEntry).catch(() => {});
    if (onComplete) onComplete(logEntry);
    return logEntry;
  }
}
